import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Box, Chip, Container, Link as MuiLink } from '@mui/material'
import { observer } from 'mobx-react-lite'

import { AcIcon, AcTypography } from '@components'
import { ICONS, LABELS } from '@constants'
import { ISearchResult } from '@typings'

import styles from './ac-search-result.module.scss'

export const AcSearchResult = observer(
  ({
    name,
    description,
    chips,
    type,
    link,
    slug,
    id,
    milestone,
    accordion_id,
    accordion_title,
    block_id,
    track_slug,
    milestone_slug,
  }: ISearchResult) => {
    const getIcon = (type: string) => {
      switch (type) {
        case 'track':
          return ICONS.STACKED_LINE
        case 'milestone':
          return ICONS.PLACE
        default:
          return ICONS.STACKED_LINE
      }
    }

    const renderContent = useMemo(() => {
      return (
        <Box
          className={styles['ac-search-result']}
          sx={({ palette }) => ({
            textDecoration: 'none',
            border: `1px solid ${palette.grey[100]}`,
            '&:hover': {
              borderColor: palette.primary.light,
              borderBottom: `2px solid ${palette.primary.light}`,
              marginBottom: 0,
            },
          })}>
          <Container
            disableGutters
            sx={({ palette }) => ({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: palette.primary.light,
            })}>
            <AcTypography
              className={styles['ac-search-result__title']}
              size="l"
              weight="bold">
              {name && name}
            </AcTypography>
            {type !== 'term' && (
              <AcIcon
                icon={
                  type === 'download' ? ICONS.DOWNLOAD : ICONS.ARROW_FORWARD
                }
              />
            )}
          </Container>
          <AcTypography>{description}</AcTypography>
          {accordion_title && (
            <Box
              sx={({ palette }) => ({
                marginTop: '0.5rem',
                color: palette.text.secondary,
                fontSize: '0.875rem',
              })}>
              <AcTypography size="s">
                <b>Gevonden in: </b>
                {accordion_title}
              </AcTypography>
            </Box>
          )}
          {chips && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                marginTop: '1rem',
                flexWrap: 'wrap',
              }}>
              {Object.entries(chips).map(chip => (
                <Chip
                  icon={<AcIcon icon={getIcon(chip[0])} />}
                  label={
                    <>
                      <b>
                        {LABELS[chip[0].toUpperCase() as keyof typeof LABELS]}:{' '}
                      </b>
                      {chip[1]}
                    </>
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      )
    }, [chips, type, name, description, accordion_title])

    return useMemo(() => {
      // Build URL with accordion parameters if available
      const buildUrl = () => {
        let baseUrl: string

        if (type === 'audience') {
          // For audience type, construct the full path:
          // /milestones/{trackSlug}/{milestoneSlug}/{audienceSlug}
          if (track_slug && milestone_slug) {
            baseUrl = `/milestones/${track_slug}/${milestone_slug}/${slug}`
          } else {
            // Fallback if slugs are missing
            baseUrl = `/milestones/${slug}`
          }
        } else {
          // For other types (track, milestone, etc.)
          baseUrl = `/${type}/${slug}`
        }

        const params = new URLSearchParams()

        if (accordion_id) {
          params.append('accordion', accordion_id.toString())
        }
        if (block_id) {
          params.append('block', block_id.toString())
        }

        // Add search term for highlighting
        const urlParams = new URLSearchParams(window.location.search)
        const searchTerm = urlParams.get('s')
        if (searchTerm) {
          params.append('highlight', searchTerm)
        }

        const finalUrl = params.toString()
          ? `${baseUrl}?${params.toString()}`
          : baseUrl

        return finalUrl
      }

      switch (type) {
        case 'download':
          return (
            <MuiLink
              sx={{
                textDecoration: 'none',
                color: 'initial',
              }}
              target="_blank"
              href={link}>
              {renderContent}
            </MuiLink>
          )
        case 'term':
          return renderContent
        default:
          return (
            <Link
              to={buildUrl()}
              style={{
                textDecoration: 'none',
                color: 'initial',
              }}>
              {renderContent}
            </Link>
          )
      }
    }, [type, slug, accordion_id, block_id, track_slug, milestone_slug])
  },
)
